import { useEffect, useMemo, useRef, useState } from "react";
import {
  Facebook, Instagram, UploadCloud, PlayCircle, Loader2, CheckCircle2, XCircle,
  ShieldCheck, Link2, KeyRound, PenSquare, MessageCircle, MessagesSquare,
  CalendarDays, UserRound, ThumbsUp, Lock, History, RefreshCcw, CalendarClock, Cloud, Search,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SectionTitle } from "@/components/shared";
import { adminApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CATEGORY_ICONS = {
  posts: PenSquare,
  comments: MessageCircle,
  messages: MessagesSquare,
  events: CalendarDays,
  profile: UserRound,
  likes_reactions: ThumbsUp,
  activity: History,
};

const SOURCE_ICONS = { facebook: Facebook, instagram: Instagram, google_takeout: Cloud };
const SOURCE_CHIPS = {
  facebook: "border-blue-500/30 bg-blue-500/10 text-blue-200",
  instagram: "border-rose-500/30 bg-rose-500/10 text-rose-200",
  google_takeout: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
};

export default function FacebookTab() {
  const [sources, setSources] = useState([]);
  const [sourceType, setSourceType] = useState("facebook");
  const [selected, setSelected] = useState({});
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState("global");
  const [maxRecords, setMaxRecords] = useState(500);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [active, setActive] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [year, setYear] = useState(null);
  const [oauth, setOauth] = useState(null);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [connectInfo, setConnectInfo] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const pollRef = useRef(null);
  const lastStatus = useRef(null);

  const currentSource = useMemo(
    () => sources.find((s) => s.key === sourceType),
    [sources, sourceType]
  );
  const categories = currentSource?.categories || [];

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          adminApi.get("/admin/social/sources"),
          adminApi.get("/admin/facebook/oauth/status"),
        ]);
        setSources(s.data.sources || []);
        setOauth(o.data);
      } catch (e) {}
    })();
    loadTimeline();
  }, []);

  // Reset category selection when source changes (all checked by default)
  useEffect(() => {
    const initial = {};
    categories.forEach((cat) => { initial[cat.key] = true; });
    setSelected(initial);
    setFile(null);
  }, [sourceType, sources]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScans = async () => {
    try {
      const r = await adminApi.get("/admin/ingestions");
      const fb = (r.data.ingestions || []).filter((i) => i.type === "facebook" || i.type === "social");
      const running = fb.find((i) => i.status === "running" || i.status === "queued");
      const current = running || fb[0] || null;
      setActive(current);
      // refresh the timeline as soon as a scan completes
      if (lastStatus.current === "running" && current?.status === "completed") {
        loadTimeline();
      }
      lastStatus.current = current?.status || null;
    } catch (e) {}
  };

  const loadTimeline = async () => {
    try {
      const r = await adminApi.get("/admin/facebook/timeline");
      setTimeline(r.data);
      const years = (r.data.years || []).map((y) => y.year).filter((y) => y > 0);
      setYear((prev) => (prev && years.includes(prev) ? prev : years[0] ?? null));
    } catch (e) {}
  };

  useEffect(() => {
    loadScans();
    pollRef.current = setInterval(loadScans, 2500);
    return () => clearInterval(pollRef.current);
  }, []);

  const pickedKeys = Object.keys(selected).filter((k) => selected[k]);

  const startScan = async () => {
    if (!file) {
      toast.error(`Choose your ${currentSource?.label || "data"} export (.zip) first.`);
      return;
    }
    if (pickedKeys.length === 0) {
      toast.error("Select at least one category to scan.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source_type", sourceType);
    fd.append("categories", pickedKeys.join(","));
    fd.append("replace_existing", replaceExisting ? "true" : "false");
    fd.append("job_id", jobId || "global");
    fd.append("max_records", String(maxRecords || 500));
    setScanning(true);
    try {
      const r = await adminApi.post("/admin/facebook/ingest", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const skipped = r.data.skipped_categories || [];
      toast.success(`${currentSource?.label} scan started. Raw export stays server-side only.`);
      if (skipped.length > 0) {
        toast.info(`Not found in this export: ${skipped.join(", ")}`);
      }
      setFile(null);
      loadScans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed to start");
    } finally {
      setScanning(false);
    }
  };

  const saveOauthConfig = async () => {
    if (!appId.trim()) {
      toast.error("Enter your Meta App ID first.");
      return;
    }
    try {
      await adminApi.post("/admin/facebook/oauth/config", { app_id: appId.trim(), app_secret: appSecret });
      toast.success("Meta app credentials saved server-side.");
      setAppSecret("");
      const o = await adminApi.get("/admin/facebook/oauth/status");
      setOauth(o.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save credentials");
    }
  };

  const connectOauth = async () => {
    try {
      const r = await adminApi.post("/admin/facebook/oauth/connect");
      setConnectInfo(r.data);
      toast.info("OAuth stub ready — see the limitation note below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect unavailable");
    }
  };

  const runSearch = async () => {
    const q = searchQ.trim();
    if (!q) {
      toast.error("Type a keyword to search your social memories.");
      return;
    }
    setSearching(true);
    try {
      const r = await adminApi.get("/admin/social/search", { params: { q, k: 12 } });
      setSearchResults(r.data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const st = active?.status;
  const perCategory = active?.per_category || {};
  const activeSource = sources.find((s) => s.key === (active?.source_type || "facebook"));
  const labelOf = (k) => activeSource?.categories?.find((c) => c.key === k)?.label || k;
  const yearBucket = (timeline?.years || []).find((y) => y.year === year);
  const undatedBucket = (timeline?.years || []).find((y) => y.year === 0);
  const datedYears = (timeline?.years || []).filter((y) => y.year > 0);

  const catFromSheet = (sheet) => (sheet || "").split("_").slice(1).join("_");

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Export scan */}
      <Card className="p-6 bg-card border-border" data-testid="fb-export-scan-card">
        <div className="flex items-center justify-between gap-2">
          <SectionTitle>Social Export Scan</SectionTitle>
          <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-200">
            social_memory
          </Badge>
        </div>

        <div className="mt-3">
          <Label className="text-xs text-muted-foreground">Source</Label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger className="mt-1 bg-secondary/40" data-testid="social-source-select">
              <SelectValue placeholder="Choose a source" />
            </SelectTrigger>
            <SelectContent>
              {sources.map((s) => {
                const Icon = SOURCE_ICONS[s.key] || Facebook;
                return (
                  <SelectItem key={s.key} value={s.key} data-testid={`social-source-option-${s.key}`}>
                    <span className="inline-flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {s.label}</span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Download your archive ({currentSource?.label || "…"} data export, <span className="font-medium text-foreground">JSON format</span>),
          then upload the ZIP here. It is scanned entirely server-side and fully redacted before embedding.
        </p>

        <label
          htmlFor="fb-upload-input"
          className="mt-3 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 transition-colors py-7 cursor-pointer"
        >
          <UploadCloud className="h-8 w-8 text-primary mb-2" />
          <div className="text-sm font-medium">{file ? file.name : `Drop or select your ${currentSource?.label || ""} export (.zip)`}</div>
          <div className="text-xs text-muted-foreground mt-1">Stored in the secure vault — never served or downloadable</div>
          <input
            id="fb-upload-input"
            data-testid="fb-upload-input"
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div className="mt-4">
          <Label className="text-xs text-muted-foreground">What should the AI ingest as memory?</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.key] || Facebook;
              return (
                <label
                  key={cat.key}
                  htmlFor={`fb-cat-${cat.key}`}
                  className={cn(
                    "flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors",
                    selected[cat.key] ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/20 hover:bg-secondary/40"
                  )}
                >
                  <Checkbox
                    id={`fb-cat-${cat.key}`}
                    data-testid={`fb-category-checkbox-${cat.key}`}
                    checked={!!selected[cat.key]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [cat.key]: !!v }))}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5 text-blue-300" /> {cat.label}
                    </span>
                    <span className="block text-[11px] text-muted-foreground leading-4 mt-0.5">{cat.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <RefreshCcw className="h-3.5 w-3.5 text-primary" /> Replace previous {currentSource?.label || ""} memories
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Re-scanning a fresh export removes outdated {currentSource?.label || ""} memories first, so the index never goes stale.
            </div>
          </div>
          <Switch
            checked={replaceExisting}
            onCheckedChange={setReplaceExisting}
            data-testid="fb-replace-switch"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs text-muted-foreground">Job / Order scope</Label>
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="fb-jobid-input" className="mt-1 bg-secondary/40" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Max records / category</Label>
            <Input type="number" value={maxRecords} onChange={(e) => setMaxRecords(Number(e.target.value))} data-testid="fb-maxrecords-input" className="mt-1 bg-secondary/40" />
          </div>
        </div>

        <Button onClick={startScan} disabled={scanning} className="w-full mt-4" data-testid="fb-scan-start-button">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Start {currentSource?.label || ""} Scan
        </Button>
      </Card>

      {/* Scan status */}
      <Card className="p-6 bg-card border-border" data-testid="fb-scan-status-card">
        <div className="flex items-center justify-between">
          <SectionTitle>Scan Status</SectionTitle>
          <div className="flex items-center gap-2">
            {active?.source_type && (
              <Badge variant="outline" className={cn("text-[11px]", SOURCE_CHIPS[active.source_type] || SOURCE_CHIPS.facebook)}>
                {activeSource?.label || active.source_type}
              </Badge>
            )}
            {st === "running" && <span className="flex items-center gap-1.5 text-xs text-primary"><Loader2 className="h-3.5 w-3.5 animate-spin" /> scanning</span>}
            {st === "completed" && <span className="flex items-center gap-1.5 text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> completed</span>}
            {st === "failed" && <span className="flex items-center gap-1.5 text-xs text-rose-300"><XCircle className="h-3.5 w-3.5" /> failed</span>}
          </div>
        </div>

        {!active ? (
          <div className="mt-6 text-sm text-muted-foreground" data-testid="fb-scan-empty-state">
            No social scan yet. Upload a data export to give the AI personas your personal history and context — always redacted, always tenant-licensed.
          </div>
        ) : (
          <>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-mono text-muted-foreground truncate">{active.source} • {active.current_sheet || "—"}</span>
                <span className="tabular-nums">{active.progress || 0}%</span>
              </div>
              <Progress value={active.progress || 0} data-testid="fb-scan-progress-bar" className="h-2" />
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4">
              <MiniStat label="Categories" value={`${active.processed_sheets || 0}/${active.total_sheets || 0}`} />
              <MiniStat label="Indexed" value={(active.rows_indexed || 0).toLocaleString()} />
              <MiniStat label="Replaced" value={(active.replaced || 0).toLocaleString()} accent="text-amber-300" />
              <MiniStat label="Redactions" value={(active.redactions || 0).toLocaleString()} accent="text-rose-300" />
            </div>
            {active.error && <div className="mt-3 text-xs text-rose-300">{active.error}</div>}

            <div className="mt-5">
              <SectionTitle>Category Coverage</SectionTitle>
              <div className="mt-3" data-testid="fb-category-coverage-list">
                {Object.entries(perCategory).map(([k, v]) => {
                  const Icon = CATEGORY_ICONS[k] || Facebook;
                  return (
                    <div key={k} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                      <span className="inline-flex items-center gap-1.5 text-sm text-blue-300">
                        <Icon className="h-3.5 w-3.5" /> {labelOf(k)}
                      </span>
                      <span className="text-sm tabular-nums text-muted-foreground">{v.toLocaleString()} memories</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div className="mt-5 flex items-start gap-2 rounded-lg border border-border bg-secondary/30 p-3">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-5">
            Zero-exposure applies to social memory too: emails, phone numbers, URLs and identifiers are
            redacted before embedding, and only tenants licensed for <span className="font-mono text-blue-200">social_memory</span> can retrieve it.
          </p>
        </div>
      </Card>

      {/* Memory search */}
      <Card className="p-6 bg-card border-border lg:col-span-2" data-testid="fb-memory-search-card">
        <SectionTitle>Memory Search</SectionTitle>
        <p className="text-xs text-muted-foreground mt-1.5">
          Semantic search across every indexed social memory — find any moment instantly by keyword or idea.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder='Try "marathon", "summit", "coffee"…'
            data-testid="fb-memory-search-input"
            className="bg-secondary/40"
          />
          <Button onClick={runSearch} disabled={searching} data-testid="fb-memory-search-button">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
          </Button>
        </div>
        {searchResults !== null && (
          <div className="mt-4 space-y-2" data-testid="fb-memory-search-results">
            {searchResults.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="fb-memory-search-empty">
                No memories matched that query.
              </div>
            ) : (
              searchResults.map((r, i) => {
                const Icon = CATEGORY_ICONS[catFromSheet(r.sheet)] || History;
                return (
                  <div key={`${r.sheet}-${r.record_id}-${i}`} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`fb-memory-search-result-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-mono text-blue-200">
                        <Icon className="h-3.5 w-3.5" /> {r.sheet}/{r.record_id}
                      </span>
                      <span className="flex items-center gap-2">
                        <Badge variant="outline" className={cn("text-[10px]", SOURCE_CHIPS[r.source_type] || SOURCE_CHIPS.facebook)}>
                          {sources.find((s) => s.key === r.source_type)?.label || r.source_type}
                        </Badge>
                        {r.date && <span className="text-[11px] text-muted-foreground tabular-nums">{r.date}</span>}
                        <span className="text-[11px] text-muted-foreground tabular-nums">match {(r.score * 100).toFixed(0)}%</span>
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-5 mt-1.5">{r.snippet}</p>
                  </div>
                );
              })
            )}
          </div>
        )}
      </Card>

      {/* Memory timeline */}
      <Card className="p-6 bg-card border-border lg:col-span-2" data-testid="fb-timeline-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <SectionTitle>Memory Timeline</SectionTitle>
            {timeline && (
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary text-[11px]" data-testid="fb-timeline-total-badge">
                {timeline.total_memories.toLocaleString()} memories
              </Badge>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={loadTimeline} data-testid="fb-timeline-refresh-button">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          Browse what the AI remembers by year — sanitized snippets only, exactly as personas see them.
        </p>

        {!timeline || timeline.total_memories === 0 ? (
          <div className="mt-5 text-sm text-muted-foreground" data-testid="fb-timeline-empty-state">
            No social memories indexed yet. Run a scan above and the timeline will fill in by year.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap mt-4" data-testid="fb-timeline-year-row">
              {datedYears.map((y) => (
                <button
                  key={y.year}
                  onClick={() => setYear(y.year)}
                  data-testid={`fb-timeline-year-${y.year}`}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    year === y.year
                      ? "border-primary/50 bg-primary/15 text-primary"
                      : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/60"
                  )}
                >
                  {y.year} <span className="tabular-nums opacity-70">({y.count})</span>
                </button>
              ))}
              {undatedBucket && (
                <button
                  onClick={() => setYear(0)}
                  data-testid="fb-timeline-year-undated"
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    year === 0
                      ? "border-primary/50 bg-primary/15 text-primary"
                      : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/60"
                  )}
                >
                  Undated <span className="tabular-nums opacity-70">({undatedBucket.count})</span>
                </button>
              )}
            </div>

            {yearBucket && (
              <div className="mt-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {Object.entries(yearBucket.by_category).map(([sheet, c]) => {
                    const Icon = CATEGORY_ICONS[catFromSheet(sheet)] || History;
                    return (
                      <span key={sheet} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/30 px-2 py-0.5 text-[11px] text-muted-foreground">
                        <Icon className="h-3 w-3" /> {sheet} <span className="tabular-nums">{c}</span>
                      </span>
                    );
                  })}
                </div>
                <ScrollArea className="mt-3 max-h-72 pr-3">
                  <div className="space-y-2" data-testid="fb-timeline-entries">
                    {yearBucket.entries.map((e, i) => {
                      const Icon = CATEGORY_ICONS[catFromSheet(e.sheet)] || History;
                      return (
                        <div key={`${e.sheet}-${e.record_id}-${i}`} className="rounded-lg border border-border bg-secondary/20 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-blue-200">
                              <Icon className="h-3.5 w-3.5" /> {e.sheet}/{e.record_id}
                            </span>
                            <span className="flex items-center gap-2">
                              <Badge variant="outline" className={cn("text-[10px]", SOURCE_CHIPS[e.source_type] || SOURCE_CHIPS.facebook)}>
                                {sources.find((s) => s.key === e.source_type)?.label || e.source_type}
                              </Badge>
                              {e.date && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
                                  <CalendarClock className="h-3 w-3" /> {e.date}
                                </span>
                              )}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-5 mt-1.5">{e.snippet}</p>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </>
        )}
      </Card>

      {/* OAuth stub (Facebook only) */}
      {sourceType === "facebook" && (
        <Card className="p-6 bg-card border-border lg:col-span-2" data-testid="fb-oauth-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <SectionTitle>Live Graph API Sync</SectionTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-200" data-testid="fb-oauth-stub-badge">
                <Lock className="h-3 w-3 mr-1" /> Stub — requires Meta App Review
              </Badge>
              {oauth?.configured && (
                <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">
                  App configured: {oauth.app_id_masked}
                </Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 max-w-3xl">
            {oauth?.note || "Meta requires a registered Developer App plus App Review for personal-history permissions. Use the Data Export upload for full history today."}
          </p>

          <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 mt-4 items-end">
            <div>
              <Label className="text-xs text-muted-foreground">Meta App ID</Label>
              <Input value={appId} onChange={(e) => setAppId(e.target.value)} placeholder={oauth?.app_id_masked || "e.g. 1234567890"} data-testid="fb-oauth-appid-input" className="mt-1 bg-secondary/40" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Meta App Secret (stored server-side only)</Label>
              <Input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder="••••••••" data-testid="fb-oauth-secret-input" className="mt-1 bg-secondary/40" />
            </div>
            <Button variant="secondary" onClick={saveOauthConfig} data-testid="fb-oauth-save-button">
              <KeyRound className="h-4 w-4" /> Save Credentials
            </Button>
            <Button onClick={connectOauth} disabled={!oauth?.configured} data-testid="fb-oauth-connect-button">
              <Link2 className="h-4 w-4" /> Connect Facebook
            </Button>
          </div>

          {connectInfo && (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="fb-oauth-connect-info">
              <div className="text-xs text-amber-200 font-medium">OAuth authorize URL (public_profile scope only)</div>
              <div className="text-xs font-mono text-muted-foreground mt-1 break-all">{connectInfo.authorize_url}</div>
              <div className="text-xs text-muted-foreground mt-2">{connectInfo.note}</div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent = "text-foreground" }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-1 text-lg font-display font-semibold tabular-nums", accent)}>{value}</div>
    </div>
  );
}
